import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import RegisterForm from "components/auth/Register/RegisterForm";
import AcademyWithoutMerchantInfo from "components/contact/AcademyWithoutMerchantInfo";
import AccessDenial from "components/contact/AccessDenial";
import NotFoundPage from "components/contact/NoFoundPage";
import Home from "components/home/Home";
import ContentContainer from "components/layout/ContentContainer";
import Layout from "components/layout/Layout";
import ChangePassword from "components/profile/ChangePassword";
import Profile from "components/profile/Profile";
import PrivateRoute from "./PrivateRoute";

import NewButtonExamples from "components/common/buttons/basicButton/NewButtonExamples";
import { setAcademyMerchantStatus } from "store/actions/academyMerchantStatus";
import { authorize } from "store/actions/auth";
import { currentAcademyID } from "store/actions/currentAcademyID";
import { RootState } from "store/store";
import { userPermissions } from "../store/actions/userPermissions";
import { DEMO_TOKEN } from "../demo/demoAuth";
import jwtDecode from "jwt-decode";

const ChangePasswordForm = lazy(() => import("components/auth/ChangePasswordForm"));
const RemindPassword = lazy(() => import("components/auth/remindPassword/RemindPassword"));
const ResetPassword = lazy(() => import("components/auth/resetPassword/ResetPassword"));
const Contact = lazy(() => import("components/contact/Contact"));
const Files = lazy(() => import("components/Files"));
// const Structure = lazy(() => import('components/structure/Structure'));
const Structure = lazy(() => import("components/structure/StructureRefactor"));

const Users = lazy(() => import("components/users/Users"));
const UserDetail = lazy(() => import("components/users/detail/UserDetail"));
const Players = lazy(() => import("components/players/academy/Players"));
const PlayersDetail = lazy(() => import("components/players/detail/PlayersDetail"));
const RequestedOutsidePlayers = lazy(
  () => import("components/players/outside/RequestedOutsidePlayers")
);
const AvailableOutsidePlayers = lazy(
  () => import("components/players/outside/AvailablePlayersOutsidePlayers")
);

const Trainers = lazy(() => import("components/trainers/Trainers"));
const TrainersDetail = lazy(() => import("components/trainers/detail/TrainersDetail"));
const TrainerOutsideDatail = lazy(() => import("components/trainers/detail/TrainerOutsideDetail"));
const RequestedTrainerOutside = lazy(
  () => import("components/trainers/detail/RequestedTrainerOutside")
);
const AvailableTrainerOutside = lazy(
  () => import("components/trainers/detail/AvailableTrainerOutside")
);

const Events = lazy(() => import("components/events/Events"));
const Concepts = lazy(() => import("components/concepts/Concepts"));
const MarketplaceEvents = lazy(() => import("components/marketpleace/Events/MarketplaceEvents"));
const EventsDetail = lazy(() => import("components/events/detail/EventsDetail"));
const DraftEvents = lazy(() => import("components/events/DraftEvents"));
const CyclicalEvents = lazy(() => import("components/events/CyclicalEvents"));

const Academies = lazy(() => import("components/academy/Academies"));
const AcademyDetail = lazy(() => import("components/academy/detail/Academy"));

const Passes = lazy(() =>
  import("components/marketpleace/Passes").then((mod) => ({
    default: mod.Passes,
  }))
);

const Categories = lazy(() => import("components/marketpleace/Categories/Categories"));
const Attributes = lazy(() => import("components/marketpleace/Attributes/Attributes"));
const Deliveries = lazy(() => import("components/marketpleace/Deliveries/Deliveries"));

const Products = lazy(() =>
  import("components/marketpleace/Products").then((mod) => ({
    default: mod.Products,
  }))
);
const ProductDetail = lazy(() =>
  import("components/marketpleace/Products").then((mod) => ({
    default: mod.ProductDetail,
  }))
);
const Orders = lazy(() =>
  import("components/marketpleace/Orders").then((mod) => ({
    default: mod.Orders,
  }))
);
const OrderDetail = lazy(() =>
  import("components/marketpleace/Orders").then((mod) => ({
    default: mod.OrderDetail,
  }))
);

const Coupon = lazy(() => import("components/marketpleace/Coupon/Coupon"));

const Chat = lazy(() => import("components/chat/Chat"));
const NotificationCenter = lazy(() => import("components/notificationCenter/NotificationCenter"));

const Posts = lazy(() =>
  import("components/Moderation/detail/Moderation").then((mod) => ({
    default: mod.default,
  }))
);

interface InicialPermissionValue {
  id: number;
  pass: boolean;
}

const initiaPermissionValue: InicialPermissionValue[] = [
  { id: 46, pass: false },
  { id: 67, pass: false },
  { id: 130, pass: false },
  { id: 131, pass: false },
  { id: 132, pass: false },
  { id: 133, pass: false },
  { id: 134, pass: false },
  { id: 135, pass: false },
  { id: 137, pass: false },
  { id: 141, pass: false },
  { id: 140, pass: false },
  { id: 139, pass: false },
  { id: 138, pass: false },
  // { id: 136, pass: false },
  { id: 160, pass: false },
  { id: 161, pass: false },
  { id: 162, pass: false },
  { id: 163, pass: false },
  { id: 164, pass: false },
  { id: 165, pass: false },
  { id: 173, pass: false },
  { id: 179, pass: false },
  { id: 182, pass: false },
  { id: 139, pass: false },
  { id: 205, pass: false },
  { id: 211, pass: false },
  { id: 213, pass: false },
  { id: 262, pass: false },
  // { id: 215, pass: false },
  { id: 318, pass: false },
  { id: 264, pass: false },
  { id: 280, pass: false },
  { id: 282, pass: false },
  { id: 283, pass: false },
  { id: 284, pass: false },
  { id: 291, pass: false },
  { id: 295, pass: false },
  { id: 373, pass: false },
  { id: 379, pass: false },
];

const AppRouter: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isChatActive] = useState(true);
  const [isAcademyMerchantRegistration] = useState(true);
  const [isTabPermission, setTabPermission] =
    useState<typeof initiaPermissionValue>(initiaPermissionValue);

  const getErrorStatus = useSelector((state: any) => state.errorReducer.code);

  //remove scroll position from home table
  localStorage.removeItem("scrollPosition");

  useEffect(() => {
    dispatch(authorize());

    const decodedToken: any = jwtDecode(DEMO_TOKEN);

    dispatch(setAcademyMerchantStatus(true));
    dispatch(userPermissions([{ Id: -1, Value: "Admin" }]));
    setTabPermission((prevPermissions) => prevPermissions.map((perm) => ({ ...perm, pass: true })));
    dispatch(
      currentAcademyID({
        AcademyId: "00000000-0000-0000-0000-000000000010",
        Role: decodedToken.Role,
        userId: decodedToken.UserId,
        academyName: "Klub piłkarski",
      })
    );
    setIsLoading(true);
  }, [dispatch]);

  const isTabActive = useMemo(
    () => (e: number) => {
      return isTabPermission.find((tab) => tab.id === e)?.pass;
    },
    [isTabPermission]
  );

  const locations = useLocation();
  const isShopTab = locations?.pathname.includes("shop");

  if (getErrorStatus === 404) {
    return <NotFoundPage />;
  }

  return (
    <Layout>
      <Switch>
        {isShopTab && isLoading && !isTabActive(140) && <AccessDenial />}

        <Route exact path="/login">
          <Redirect to="/" />
        </Route>

        <Route exact path="/changepassword">
          <Suspense fallback={null}>
            <ChangePasswordForm />
          </Suspense>
        </Route>

        <Route exact path="/remindPassword">
          <Suspense fallback={null}>
            <RemindPassword />
          </Suspense>
        </Route>

        <Route exact path="/resetPassword">
          <Suspense fallback={null}>
            <ResetPassword />
          </Suspense>
        </Route>

        <PrivateRoute path="/">
          <Suspense fallback={null}>
            <Switch>
              <Route exact path="/">
                <Home />
              </Route>

              <Route exact path="/academy">
                {isLoading ? isTabActive(291) ? <Academies /> : <AccessDenial /> : null}
              </Route>
              <Route exact path="/academy/detail/:id">
                {isLoading ? (
                  isTabActive(130) && isTabActive(291) ? (
                    <AcademyDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/files">
                {isLoading ? isTabActive(213) ? <Files /> : <AccessDenial /> : null}
              </Route>
              <Route exact path="/contacts">
                {isLoading ? isTabActive(211) ? <Contact /> : <AccessDenial /> : null}
              </Route>
              <Route exact path="/concepts">
                <Concepts />
              </Route>
              <Route exact path="/structure">
                {isLoading ? isTabActive(139) ? <Structure /> : <AccessDenial /> : null}
              </Route>

              <Route exact path="/users/detail/:id/academy/:id">
                <UserDetail />
              </Route>
              <Route exact path="/users">
                <Users />
              </Route>

              <Route exact path="/posts">
                {isLoading ? isTabActive(379) ? <Posts /> : <AccessDenial /> : null}
              </Route>

              <Route exact path="/przyciski">
                <NewButtonExamples />
              </Route>
              <Route exact path="/players">
                {isLoading ? isTabActive(133) ? <Players /> : <AccessDenial /> : null}
              </Route>
              <Route exact path="/players/detail/:id/academy/:academyId">
                {isLoading ? (
                  (isTabActive(134) && isTabActive(133)) ||
                  (isTabActive(205) && isTabActive(135)) ? (
                    <PlayersDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/outside-players/requested">
                {isLoading ? (
                  isTabActive(135) && isTabActive(262) ? (
                    <RequestedOutsidePlayers />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/outside-players/available">
                {isLoading ? (
                  isTabActive(282) && isTabActive(135) ? (
                    <AvailableOutsidePlayers />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/outside-players/detail/:id/academy/:id">
                {isLoading ? (
                  (isTabActive(134) && isTabActive(133) && isTabActive(135)) ||
                  (isTabActive(279) && isTabActive(262) && isTabActive(135)) ? (
                    <PlayersDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/outside-trainers/requested">
                {isLoading ? (
                  isTabActive(264) && isTabActive(138) ? (
                    <RequestedTrainerOutside />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/outside-trainers/available">
                {isLoading ? (
                  isTabActive(283) && isTabActive(138) ? (
                    <AvailableTrainerOutside />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/outside-trainers/detail/:id/academy/:academyId">
                {isLoading ? (
                  isTabActive(264) && isTabActive(280) ? (
                    <TrainerOutsideDatail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/trainers">
                {isLoading ? isTabActive(67) ? <Trainers /> : <AccessDenial /> : null}
              </Route>
              <Route exact path="/trainers/detail/:id/academy/:academyId">
                {isLoading ? (
                  (isTabActive(137) && isTabActive(67)) ||
                  (isTabActive(182) && isTabActive(283)) ? (
                    <TrainersDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/events">
                {isLoading ? (
                  isTabActive(131) && isTabActive(284) ? (
                    <Events />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/drafts">
                {isLoading ? (
                  isTabActive(295) && isTabActive(284) ? (
                    <DraftEvents />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/cyclical">
                {isLoading ? (
                  isTabActive(173) && isTabActive(284) ? (
                    <CyclicalEvents />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/events/detail/:id/academy/:academyId">
                {isLoading ? (
                  isTabActive(132) && isTabActive(284) ? (
                    <EventsDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/shop">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(140) ? (
                    <Passes />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/shop/coupon">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(373) && isTabActive(140) ? (
                    <Coupon />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/shop/passes">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(161) && isTabActive(140) ? (
                    <Passes />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/orders">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(160) && isTabActive(140) ? (
                    <Orders />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/orders/detail/:id/academy/:academyId">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(141) && isTabActive(140) ? (
                    <OrderDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/products">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(162) && isTabActive(140) ? (
                    <Products />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/products/detail/:id/academy/:academyId">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(46) && isTabActive(140) ? (
                    <ProductDetail />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/categories">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(163) && isTabActive(140) ? (
                    <Categories />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/events">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(163) && isTabActive(140) ? (
                    <MarketplaceEvents />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/attributes">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(164) && isTabActive(140) ? (
                    <Attributes />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>
              <Route exact path="/shop/delivery">
                {!isAcademyMerchantRegistration && isLoading ? (
                  <AcademyWithoutMerchantInfo />
                ) : isLoading ? (
                  isTabActive(165) && isTabActive(140) ? (
                    <Deliveries />
                  ) : (
                    <AccessDenial />
                  )
                ) : null}
              </Route>

              <Route exact path="/users/add">
                <RegisterForm />
              </Route>
              <Route exact path="/notifications">
                <NotificationCenter />
              </Route>
              <Route exact path="/chat">
                {isLoading ? isChatActive ? <Chat /> : <AccessDenial /> : null}
              </Route>
              <Route path="/profile">
                <Profile />
                <Switch>
                  <Route exact path="/profile/changePassword">
                    <ContentContainer title="Zmień hasło">
                      <ChangePassword />
                    </ContentContainer>
                  </Route>
                </Switch>
              </Route>

              <Route path="*">
                <NotFoundPage />
              </Route>
            </Switch>
          </Suspense>
        </PrivateRoute>

        {/* <Route path="*">
          <NotFoundPage />
        </Route> */}
      </Switch>
    </Layout>
  );
};

export default AppRouter;
